import type { PackedGraphemeStrokes } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.strokes.entities";
import { isPlainObject } from "@-/com.npmjs.is-what";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

const HEX_STRING_RE = /^[\da-f]*$/i;
const NUMERIC_OBJECT_KEY_RE = /^\d+$/;

export type UnpackedGraphemeStrokes = {
  medians: [number, number][];
  path: string;
};

/** Turns `PackedGraphemeStrokes` (flat `x,y,x,y,…` after the SVG path) into coordinate pairs. */
export function parsePackedGraphemeStrokes(packed: PackedGraphemeStrokes): UnpackedGraphemeStrokes {
  const [path, ...flatMedianList] = packed;
  const medians: [number, number][] = [];
  for (let i = 0; i + 1 < arrayLength(flatMedianList); i += 2) {
    const x = flatMedianList[i];
    const y = flatMedianList[i + 1];
    if (x === undefined || y === undefined) {
      break;
    }
    medians.push([x, y]);
  }
  return { medians, path };
}

/** Packs `{ path, medians }` into `PackedGraphemeStrokes` (SVG path then flat `x,y,x,y,…`). */
export function packGraphemeStrokes({
  medians,
  path,
}: UnpackedGraphemeStrokes): PackedGraphemeStrokes {
  const flatMedianList: number[] = [];
  for (const [x, y] of medians) {
    flatMedianList.push(x, y);
  }
  return [path, ...flatMedianList];
}

/** Normalizes SQLite BLOB values from native drivers, relational queries, and the web static-db worker. */
export function coerceDriverBlobToUint8Array(value: unknown): Uint8Array | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    if (value.length % 2 !== 0 || !HEX_STRING_RE.test(value)) {
      return null;
    }
    const bytes = new Uint8Array(value.length / 2);
    for (let i = 0; i < value.length; i += 2) {
      bytes[i / 2] = Number.parseInt(value.slice(i, i + 2), 16);
    }
    return bytes;
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  if (typeof Buffer !== "undefined" && Buffer.isBuffer(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (Array.isArray(value)) {
    if (hasLength(value, 0)) {
      return new Uint8Array(0);
    }
    const bytes: number[] = [];
    for (const entry of value) {
      if (typeof entry !== "number" || !Number.isFinite(entry) || entry < 0 || entry > 255) {
        return null;
      }
      bytes.push(Math.trunc(entry));
    }
    return Uint8Array.from(bytes);
  }
  if (isPlainObject(value)) {
    const record = value;
    const keys = Object.keys(record).filter((key) => NUMERIC_OBJECT_KEY_RE.test(key));
    if (hasLength(keys, 0)) {
      return null;
    }
    keys.sort((a, b) => Number(a) - Number(b));
    const bytes: number[] = [];
    for (const key of keys) {
      const entry = Reflect.get(record, key);
      if (typeof entry !== "number" || !Number.isFinite(entry) || entry < 0 || entry > 255) {
        return null;
      }
      bytes.push(Math.trunc(entry));
    }
    return Uint8Array.from(bytes);
  }
  return null;
}
